import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Save, ArrowLeft, CheckCircle, XCircle, FileText, Plus, Trash2, RefreshCw } from 'lucide-react';
import { patients, staff, insurance, assessments } from '../services/api';

const PatientEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [patient, setPatient] = useState(null);
  const [therapists, setTherapists] = useState([]);
  const [insuranceProviders, setInsuranceProviders] = useState([]);
  const [protocols, setProtocols] = useState([]);
  const [existingAssessments, setExistingAssessments] = useState([]);
  const [assessmentData, setAssessmentData] = useState({
    protocolId: '',
    assessmentFile: null,
    comments: ''
  });
  const [deleteConfirmation, setDeleteConfirmation] = useState({
    isOpen: false,
    assessmentId: null,
    protocolName: '',
    filename: ''
  });
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    startDate: '',
    gender: '',
    primaryDiagnosis: '',
    secondaryDiagnoses: [],
    primaryTherapist: '',
    insuranceProvider: '',
    insuranceId: '',
    status: 'active',
    weeklyHours: ''
  });
  const [extractingText, setExtractingText] = useState(false); // Deprecated - remove later

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [patientResponse, therapistsResponse, insuranceResponse, protocolsResponse, patientAssessments] = await Promise.all([
          patients.getById(id),
          staff.getAllTherapists(),
          insurance.getAll(),
          assessments.getProtocols(),
          assessments.getByPatient(id)
        ]);

        console.log('Patient data:', patientResponse);
        console.log('Therapists data:', therapistsResponse);
        console.log('Insurance data:', insuranceResponse);

        // Extract data from responses
        const patientData = patientResponse.data;
        
        // For therapists, check both possible response formats
        let therapistsData = [];
        if (therapistsResponse.data?.status === 'success') {
          therapistsData = therapistsResponse.data.data || [];
        } else if (Array.isArray(therapistsResponse.data)) {
          therapistsData = therapistsResponse.data;
        }

        // For insurance, check both possible response formats
        let insuranceData = [];
        if (insuranceResponse.data?.status === 'success') {
          insuranceData = insuranceResponse.data.data || [];
        } else if (Array.isArray(insuranceResponse.data)) {
          insuranceData = insuranceResponse.data;
        }

        // For protocols, check both possible response formats
        let protocolsData = [];
        if (protocolsResponse.data?.status === 'success') {
          protocolsData = protocolsResponse.data.data || [];
        } else if (Array.isArray(protocolsResponse.data)) {
          protocolsData = protocolsResponse.data;
        }

        // For assessments, check both possible response formats
        let assessmentsData = [];
        if (patientAssessments.data?.status === 'success') {
          assessmentsData = patientAssessments.data.data || [];
        } else if (Array.isArray(patientAssessments.data)) {
          assessmentsData = patientAssessments.data;
        }

        setPatient(patientData);
        setTherapists(therapistsData);
        setInsuranceProviders(insuranceData);
        setProtocols(protocolsData);
        setExistingAssessments(assessmentsData);

        // Update form data with patient info
        setFormData({
          firstName: patientData.firstName || '',
          lastName: patientData.lastName || '',
          dateOfBirth: normalizeDateToInput(patientData.dateOfBirth) || '',
          startDate: normalizeDateToInput(patientData.startDate) || '',
          gender: patientData.gender || '',
          primaryDiagnosis: patientData.primaryDiagnosis || '',
          secondaryDiagnoses: patientData.secondaryDiagnoses || [],
          primaryTherapist: patientData.primaryTherapist || '',
          insuranceProvider: patientData.insuranceProvider || '',
          insuranceId: patientData.insuranceId || '',
          status: patientData.status || 'active',
          weeklyHours: patientData.weeklyHours || ''
        });
      } catch (error) {
        console.error('Error fetching data:', error);
        setError('Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAssessmentChange = (e) => {
    const { name, value } = e.target;
    setAssessmentData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setAssessmentData(prev => ({
      ...prev,
      assessmentFile: file
    }));
  };

  const [assessmentUploaded, setAssessmentUploaded] = useState(false);
  const [reanalyzingId, setReanalyzingId] = useState(null);

  const handleReanalyzeAssessment = async (e, assessmentId, protocolName) => {
    e.preventDefault();
    try {
      setReanalyzingId(assessmentId);
      setError(null);

      await assessments.analyze(assessmentId);

      setSuccess(`Protocolo "${protocolName}" re-analisado com sucesso!`);

      // Refresh the assessments list
      const response = await assessments.getByPatient(id);
      const assessmentsData = response.data?.data || response.data || [];
      setExistingAssessments(Array.isArray(assessmentsData) ? assessmentsData : []);
    } catch (error) {
      console.error('Error re-analyzing assessment:', error);
      setError('Falha ao re-analisar o protocolo. Tente novamente.');
    } finally {
      setReanalyzingId(null);
    }
  };

  const handleAnalyzeAssessment = async () => {
    if (!assessmentData.assessmentFile || !assessmentData.protocolId) {
      setError('Selecione o tipo de protocolo e o arquivo antes de enviar.');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Upload the assessment
      const formData = new FormData();
      formData.append('assessmentFile', assessmentData.assessmentFile);
      formData.append('protocolId', assessmentData.protocolId);
      formData.append('comments', assessmentData.comments || '');

      const uploadResponse = await assessments.upload(id, formData);
      const assessmentId = uploadResponse.data.data.id;

      // Analyze it (extract text, clean PII, parse protocol)
      const analyzeResponse = await assessments.analyze(assessmentId);

      setSuccess('Avaliação enviada e analisada com sucesso!');
      setAssessmentUploaded(true);

      // Clear the form so Save doesn't re-upload
      setAssessmentData({
        protocolId: '',
        assessmentFile: null,
        comments: ''
      });

      // Clear file input
      const fileInput = document.querySelector('input[name="assessmentFile"]');
      if (fileInput) fileInput.value = '';

    } catch (error) {
      console.error('Error analyzing assessment:', error);
      setError('Falha ao enviar ou analisar a avaliação. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);
      // Debug: log formData before sending
      console.log('Submitting formData:', formData);
      // Use FormData for file upload
      const formDataToSend = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        if (key === 'laudoFile' && value) {
          formDataToSend.append('laudoFile', value); // file object
        } else if (Array.isArray(value)) {
          formDataToSend.append(key, JSON.stringify(value)); // Send as JSON string
        } else if (value !== undefined && value !== null) {
          formDataToSend.append(key, value);
        }
      });
      await patients.update(id, formDataToSend); // patients.update should handle FormData
      
      // Handle assessment upload if provided and not already uploaded via Analyze
      if (assessmentData.assessmentFile && assessmentData.protocolId && !assessmentUploaded) {
        try {
          const assessmentFormData = new FormData();
          assessmentFormData.append('assessmentFile', assessmentData.assessmentFile);
          assessmentFormData.append('protocolId', assessmentData.protocolId);
          assessmentFormData.append('comments', assessmentData.comments || '');

          await assessments.upload(id, assessmentFormData);
          setSuccess('Informações do paciente e avaliação atualizadas com sucesso');
        } catch (assessmentError) {
          console.error('Error uploading assessment:', assessmentError);
          setSuccess('Informações do paciente atualizadas, mas o upload da avaliação falhou');
        }
      } else {
        setSuccess('Informações do paciente atualizadas com sucesso');
      }
      setAssessmentUploaded(false);
      
      // Refresh patient data to show updated information including uploaded laudo
      const patientResponse = await patients.getById(id);
      setPatient(patientResponse.data);
      
      // Clear any file selection to show the file is now saved
      document.querySelector('input[name="laudoFile"]').value = '';
    } catch (error) {
      console.error('Error updating patient:', error);
      setError('Failed to update patient information. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Deprecated function - will be removed later
  const handleExtractText = async () => {
    setExtractingText(true);
    setError(null);
    setSuccess(null);
    try {
      const result = await patients.getLaudoText(id);
      setSuccess('Texto extraído e salvo no banco de dados com sucesso!');
      const patientResponse = await patients.getById(id);
      setPatient(patientResponse.data);
    } catch (err) {
      setError('Erro ao extrair texto do laudo.');
    } finally {
      setExtractingText(false);
    }
  };

  const handleDownloadAssessment = async (e, assessmentId, filename) => {
    e.preventDefault();
    e.stopPropagation();

    try {
      const response = await assessments.download(assessmentId);
      const blob = response.data;

      // Read the first bytes to verify this is actually a PDF/valid file,
      // not a JSON error response or HTML from CloudFront SPA fallback
      const headerBytes = await blob.slice(0, 512).text();
      const isRealFile = headerBytes.startsWith('%PDF') || blob.type === 'application/pdf';

      if (!isRealFile) {
        // Try to parse as JSON error from backend
        try {
          const text = await blob.text();
          const errorData = JSON.parse(text);
          setError(errorData.message || 'Arquivo do protocolo não encontrado no servidor.');
        } catch {
          setError('Arquivo do protocolo não encontrado no servidor. Por favor, faça o upload novamente.');
        }
        return;
      }

      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading assessment:', error);
      setError('Falha ao baixar o arquivo do protocolo.');
    }
  };

  const handleDeleteAssessment = (e, assessmentId, protocolName, filename) => {
    e.preventDefault();
    e.stopPropagation();

    // Open confirmation modal
    setDeleteConfirmation({
      isOpen: true,
      assessmentId,
      protocolName,
      filename
    });
  };

  const confirmDeleteAssessment = async () => {
    const { assessmentId } = deleteConfirmation;

    // Close modal
    setDeleteConfirmation({
      isOpen: false,
      assessmentId: null,
      protocolName: '',
      filename: ''
    });

    try {
      await assessments.delete(assessmentId);
      setSuccess('Protocolo deletado com sucesso');

      // Refresh assessments list
      const patientAssessments = await assessments.getByPatient(id);
      let assessmentsData = [];
      if (patientAssessments.data?.status === 'success') {
        assessmentsData = patientAssessments.data.data || [];
      } else if (Array.isArray(patientAssessments.data)) {
        assessmentsData = patientAssessments.data;
      }
      setExistingAssessments(assessmentsData);
    } catch (error) {
      console.error('Error deleting assessment:', error);
      setError('Falha ao deletar o protocolo. Por favor, tente novamente.');
    }
  };

  const cancelDeleteAssessment = () => {
    setDeleteConfirmation({
      isOpen: false,
      assessmentId: null,
      protocolName: '',
      filename: ''
    });
  };

  // Notification component
  const Notification = ({ type, message }) => {
    if (!message) return null;

    const bgColor = type === 'success' ? 'bg-green-50' : 'bg-red-50';
    const textColor = type === 'success' ? 'text-green-800' : 'text-red-800';
    const Icon = type === 'success' ? CheckCircle : XCircle;

    return (
      <div className={`${bgColor} p-4 rounded-md mb-4 flex items-center`}>
        <Icon className={`${textColor} w-5 h-5 mr-2`} />
        <span className={textColor}>{message}</span>
      </div>
    );
  };

  // Delete Confirmation Modal
  const DeleteConfirmationModal = ({ isOpen, protocolName, filename, onConfirm, onCancel }) => {
    if (!isOpen) return null;

    return (
      <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
        {/* Background overlay */}
        <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <div
            className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
            aria-hidden="true"
            onClick={onCancel}
          ></div>

          {/* Center modal */}
          <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

          <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                  <Trash2 className="h-6 w-6 text-red-600" aria-hidden="true" />
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                  <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                    Deletar Protocolo de Avaliação
                  </h3>
                  <div className="mt-4">
                    <p className="text-sm text-gray-500 mb-3">
                      Tem certeza que deseja deletar este protocolo? Esta ação não pode ser desfeita.
                    </p>
                    <div className="bg-gray-50 rounded-md p-3 space-y-2">
                      <div className="flex items-start">
                        <span className="text-xs font-semibold text-gray-700 w-20">Protocolo:</span>
                        <span className="text-xs text-gray-900 flex-1">{protocolName}</span>
                      </div>
                      <div className="flex items-start">
                        <span className="text-xs font-semibold text-gray-700 w-20">Arquivo:</span>
                        <span className="text-xs text-gray-900 flex-1 break-all">{filename}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <button
                type="button"
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm transition-colors duration-200"
                onClick={onConfirm}
              >
                Deletar Protocolo
              </button>
              <button
                type="button"
                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm transition-colors duration-200"
                onClick={onCancel}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Helper to format date as DD/MM/YYYY
  function formatDateDisplay(dateStr) {
    if (!dateStr) return '';
    const [year, month, day] = dateStr.split('-');
    return `${day}/${month}/${year}`;
  }

  // Helper to normalize DD/MM/YYYY to YYYY-MM-DD for input fields
  function normalizeDateToInput(dateStr) {
    if (!dateStr) return '';
    if (dateStr.includes('-')) return dateStr; // already normalized
    const [day, month, year] = dateStr.split('/');
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  }

  if (loading && !success) {
    return <div className="p-6">Loading...</div>;
  }

  // Find the current therapist and insurance provider objects
  const currentTherapist = therapists.find(t => t.id === formData.primaryTherapist);
  const currentInsurance = insuranceProviders.find(i => i.id === formData.insuranceProvider);

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={deleteConfirmation.isOpen}
        protocolName={deleteConfirmation.protocolName}
        filename={deleteConfirmation.filename}
        onConfirm={confirmDeleteAssessment}
        onCancel={cancelDeleteAssessment}
      />

      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <button
            onClick={() => navigate(`/dashboard/patients/${id}`)}
            className="mr-4 text-gray-600 hover:text-brand-600 transition-colors duration-200 p-2 rounded-full hover:bg-brand-50"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Editar Paciente</h1>
            <p className="text-gray-600 mt-1">Atualizar informações do paciente</p>
          </div>
        </div>
      </div>

      {/* Notifications */}
      {success && <Notification type="success" message="Informações do paciente atualizadas com sucesso" />}
      {error && <Notification type="error" message={error} />}

      <div className="bg-white rounded-lg shadow-md border border-gray-200 p-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700">Nome</label>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 transition-all duration-200"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Sobrenome</label>
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 transition-all duration-200"
                required
              />
            </div>
            <div>
              <label className="block mb-2 font-medium text-gray-700" htmlFor="dateOfBirth">
                Data de nascimento
              </label>
              <input
                type="date"
                id="dateOfBirth"
                name="dateOfBirth"
                value={formData.dateOfBirth}
                onChange={handleChange}
                className="mb-4 w-full border border-gray-300 rounded-lg px-3 py-2 focus:border-brand-500 focus:ring-brand-500 transition-all duration-200"
              />
              <div className="text-xs text-gray-500 mb-2">
                {formData.dateOfBirth && `Selecionado: ${formatDateDisplay(formData.dateOfBirth)}`}
              </div>
            </div>
            <div>
              <label className="block mb-2 font-medium text-gray-700" htmlFor="startDate">
                Data de início
              </label>
              <input
                type="date"
                id="startDate"
                name="startDate"
                value={formData.startDate}
                onChange={handleChange}
                className="mb-4 w-full border border-gray-300 rounded-lg px-3 py-2 focus:border-brand-500 focus:ring-brand-500 transition-all duration-200"
              />
              <div className="text-xs text-gray-500 mb-2">
                {formData.startDate && `Selecionado: ${formatDateDisplay(formData.startDate)}`}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Gênero</label>
              <select
                name="gender"
                value={formData.gender}
                onChange={handleChange}
                className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 transition-all duration-200"
                required
              >
                <option value="">Selecione o gênero</option>
                <option value="Male">Masculino</option>
                <option value="Female">Feminino</option>
                <option value="Other">Outro</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Diagnóstico Principal</label>
              <input
                type="text"
                name="primaryDiagnosis"
                value={formData.primaryDiagnosis}
                onChange={handleChange}
                className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 transition-all duration-200"
              />
            </div>
            {/* Laudo (Medical Report) Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700">Laudo Médico (PDF ou Imagem)</label>
              
              {/* Show existing laudo file if available */}
              {patient?.laudoFile && (
                <div className="mt-2 p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                  <div className="flex items-center">
                    <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center mr-3">
                      <CheckCircle className="text-emerald-600" size={16} />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-emerald-700">
                        Laudo salvo: {patient.laudoFile}
                      </p>
                      {patient.laudoExtractedText && (
                        <p className="text-xs text-emerald-600 mt-1">
                          Texto extraído disponível ({patient.laudoExtractedText.length} caracteres)
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
              
              <input
                type="file"
                name="laudoFile"
                accept=".pdf,image/*"
                onChange={e => setFormData(prev => ({ ...prev, laudoFile: e.target.files[0] }))}
                className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 transition-all duration-200"
              />
              {formData.laudoFile && (
                <p className="mt-2 text-xs text-brand-600">
                  📎 Novo arquivo selecionado: {formData.laudoFile.name} (clique em Salvar para substituir)
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Terapeuta Responsável</label>
              <select
                name="primaryTherapist"
                value={formData.primaryTherapist}
                onChange={handleChange}
                className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 transition-all duration-200"
              >
                <option value="">Selecione o Terapeuta</option>
                {therapists.map(therapist => (
                  <option key={therapist.id} value={therapist.id}>
                    {`${therapist.firstName} ${therapist.lastName}`}
                  </option>
                ))}
              </select>
              {currentTherapist && (
                <p className="mt-1 text-sm text-gray-500">
                  Terapeuta atual: {currentTherapist.firstName} {currentTherapist.lastName}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Horas Semanais de Terapia</label>
              <input
                type="number"
                name="weeklyHours"
                value={formData.weeklyHours}
                onChange={handleChange}
                min="1"
                max="168"
                placeholder="Ex: 20"
                className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 transition-all duration-200"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Convênio</label>
              <select
                name="insuranceProvider"
                value={formData.insuranceProvider}
                onChange={handleChange}
                className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 transition-all duration-200"
              >
                <option value="">Selecione o Convênio</option>
                {insuranceProviders.map(provider => (
                  <option key={provider.id} value={provider.id}>
                    {provider.name}
                  </option>
                ))}
              </select>
              {currentInsurance && (
                <p className="mt-1 text-sm text-gray-500">
                  Convênio atual: {currentInsurance.name}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Número do Convênio</label>
              <input
                type="text"
                name="insuranceId"
                value={formData.insuranceId}
                onChange={handleChange}
                className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 transition-all duration-200"
                placeholder="Enter insurance ID"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Status</label>
              <div className="mt-1 space-y-2">
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 transition-all duration-200"
                  required
                >
                  <option value="active">Ativo</option>
                  <option value="inactive">Inativo</option>
                </select>
                <p className="text-sm text-gray-500">
                  {formData.status === 'active' 
                    ? 'Active patients are currently receiving services and are included in regular scheduling.'
                    : 'Inactive patients are not currently receiving services but their records are maintained in the system.'}
                </p>
              </div>
            </div>
          </div>

          {/* Existing Assessments Section */}
          {existingAssessments.length > 0 && (
            <div className="bg-white shadow-md rounded-lg border border-gray-200 p-6 mt-8">
              <div className="flex items-center mb-4">
                <div className="w-8 h-8 rounded-full bg-brand-100 flex items-center justify-center mr-3">
                  <FileText className="text-brand-600" size={16} />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Protocolos Existentes
                </h3>
              </div>
              <div className="space-y-3">
                {existingAssessments.map((assessment) => (
                  <div key={assessment.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:border-brand-200 hover:bg-brand-50 transition-all duration-200">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-r from-brand-600 to-brand-500 flex items-center justify-center">
                          <FileText className="text-white" size={16} />
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-gray-900">
                            {assessment.protocol?.name || 'Unknown Protocol'}
                          </p>
                          <p className="text-xs text-gray-500">
                            File: {assessment.originalFilename}
                          </p>
                          <p className="text-xs text-gray-400">
                            Version {assessment.version} • Uploaded: {new Date(assessment.uploadTime).toLocaleDateString()}
                          </p>
                          {assessment.comments && (
                            <p className="text-xs text-gray-600 mt-1">
                              Comments: {assessment.comments}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        type="button"
                        onClick={(e) => handleDownloadAssessment(e, assessment.id, assessment.originalFilename)}
                        className="inline-flex items-center px-3 py-2 border border-brand-300 rounded-lg text-sm font-medium text-brand-700 bg-brand-50 hover:bg-brand-100 transition-all duration-200"
                      >
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        Download
                      </button>
                      <button
                        type="button"
                        onClick={(e) => handleReanalyzeAssessment(e, assessment.id, assessment.protocol?.name || 'Unknown')}
                        disabled={reanalyzingId === assessment.id}
                        className={`inline-flex items-center px-3 py-2 border rounded-lg text-sm font-medium transition-all duration-200 ${
                          reanalyzingId === assessment.id
                            ? 'border-gray-300 text-gray-400 bg-gray-50 cursor-not-allowed'
                            : 'border-amber-300 text-amber-700 bg-amber-50 hover:bg-amber-100'
                        }`}
                        title="Re-analisar protocolo"
                      >
                        <RefreshCw className={`w-4 h-4 mr-1 ${reanalyzingId === assessment.id ? 'animate-spin' : ''}`} />
                        {reanalyzingId === assessment.id ? 'Analisando...' : 'Re-analisar'}
                      </button>
                      <button
                        type="button"
                        onClick={(e) => handleDeleteAssessment(e, assessment.id, assessment.protocol?.name || 'Unknown', assessment.originalFilename)}
                        className="inline-flex items-center px-3 py-2 border border-red-300 rounded-lg text-sm font-medium text-red-700 bg-red-50 hover:bg-red-100 transition-all duration-200"
                        title="Deletar protocolo"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                      {assessment.extractedText && (
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800 border border-emerald-200">
                          Analisado
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Assessment Upload Section */}
          <div className="bg-white shadow-md rounded-lg border border-gray-200 p-6 mt-8">
            <div className="flex items-center mb-4">
              <div className="w-8 h-8 rounded-full bg-gradient-to-r from-brand-600 to-brand-500 flex items-center justify-center mr-3">
                <Plus className="text-white" size={16} />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">
                Carregar Novo Protocolo (Opcional)
              </h3>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Faça o upload dos resultados do protocolo para habilitar análise com IA
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tipo de Protocolo
                </label>
                <select
                  name="protocolId"
                  value={assessmentData.protocolId}
                  onChange={handleAssessmentChange}
                  className="w-full border-gray-300 rounded-lg shadow-sm focus:ring-brand-500 focus:border-brand-500 transition-all duration-200"
                >
                  <option value="">Selecionar Tipo de Protocolo</option>
                  {protocols.map((protocol) => (
                    <option key={protocol.id} value={protocol.id}>
                      {protocol.name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Arquivo do Protocolo
                </label>
                <input
                  type="file"
                  name="assessmentFile"
                  onChange={handleFileChange}
                  accept=".pdf,.doc,.docx"
                  className="w-full border-gray-300 rounded-lg shadow-sm focus:ring-brand-500 focus:border-brand-500 transition-all duration-200"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Formatos suportados: PDF, DOC, DOCX (Máx 10MB)
                </p>
              </div>
            </div>
            
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Comentários (Opcional)
              </label>
              <textarea
                name="comments"
                value={assessmentData.comments}
                onChange={handleAssessmentChange}
                rows={2}
                placeholder="Quaisquer notas adicionais sobre este protocolo..."
                className="w-full border-gray-300 rounded-lg shadow-sm focus:ring-brand-500 focus:border-brand-500 transition-all duration-200"
              />
            </div>
            
            {/* Analyze Button - Show when file is selected */}
            {assessmentData.assessmentFile && assessmentData.protocolId && (
              <div className="mt-4">
                <button
                  type="button"
                  onClick={handleAnalyzeAssessment}
                  disabled={loading}
                  className="flex items-center px-4 py-2 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-lg text-sm font-medium hover:from-emerald-700 hover:to-teal-700 disabled:opacity-50 transition-all duration-200 shadow-sm"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                  Enviar e Analisar Protocolo
                </button>
                <p className="text-xs text-gray-500 mt-1">
                  Envia o arquivo, extrai o texto e processa o protocolo automaticamente
                </p>
              </div>
            )}
          </div>

          <div className="flex justify-end space-x-4 mt-8 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={() => navigate(`/dashboard/patients/${id}`)}
              className="px-6 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-all duration-200"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center px-6 py-2 bg-gradient-to-r from-brand-700 to-brand-600 text-white text-sm font-medium rounded-lg hover:from-brand-800 hover:to-brand-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 disabled:opacity-50 transition-all duration-200 shadow-sm"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Salvando...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" /> Salvar Alterações
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PatientEdit; 